import { ModelDataType } from 'src/app/interfaces/model-data-type';

import { BaseModel } from './base-model';
import { InsurancePolicySite } from './insurance-policy-site';
import { Site } from './site';

export class InsurancePolicy extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static InsurancePolicyProperties: (
    | {
        property: keyof InsurancePolicy;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof InsurancePolicy;
        type: 'alias';
      }
    | {
        property: keyof InsurancePolicy;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'InsuranceCertificateId', type: 'column', dataType: 'number' },
    { property: 'policyNumber', type: 'column', dataType: 'string' },
    { property: 'policyType', type: 'column', dataType: 'string' },
    { property: 'effectiveDate', type: 'column', dataType: 'date' },
    { property: 'expirationDate', type: 'column', dataType: 'date' },
    { property: 'status', type: 'column', dataType: 'string' },
    { property: 'statusDate', type: 'column', dataType: 'date' },
    { property: 'insurer', type: 'column', dataType: 'string' },
    { property: 'insurerLetter', type: 'column', dataType: 'string' },
    { property: 'naic', type: 'column', dataType: 'string' },
    { property: 'aMBestCompanyNumber', type: 'column', dataType: 'string' },
    { property: 'amBestRating', type: 'column', dataType: 'string' },
    { property: 'financialSize', type: 'column', dataType: 'string' },
    { property: 'addlInsured', type: 'column', dataType: 'string' },
    { property: 'subrWvd', type: 'column', dataType: 'boolean' },
    { property: 'isRequired', type: 'column', dataType: 'boolean' },
    { property: 'hasPolicyDoc', type: 'column', dataType: 'boolean' },
    { property: 'hasRequiredForms', type: 'column', dataType: 'boolean' },
    { property: 'perOccurence', type: 'column', dataType: 'number' },
    { property: 'aggregate', type: 'column', dataType: 'number' },
    { property: 'hasAiOngoingOps', type: 'column', dataType: 'boolean' },
    { property: 'hasAiCompOps', type: 'column', dataType: 'boolean' },
    { property: 'hasPnc', type: 'column', dataType: 'boolean' },
    { property: 'damageToRented', type: 'column', dataType: 'number' },
    { property: 'medicalExpenses', type: 'column', dataType: 'number' },
    { property: 'personalAdvInjury', type: 'column', dataType: 'number' },
    { property: 'productOps', type: 'column', dataType: 'number' },
    { property: 'isProdOpsSameAsAgg', type: 'column', dataType: 'boolean' },
    { property: 'pollution', type: 'column', dataType: 'number' },
    { property: 'combinedSingle', type: 'column', dataType: 'number' },
    { property: 'biPerPerson', type: 'column', dataType: 'number' },
    { property: 'biPerAccident', type: 'column', dataType: 'number' },
    { property: 'propertyDamage', type: 'column', dataType: 'number' },
    { property: 'isCommercialAuto', type: 'column', dataType: 'boolean' },
    { property: 'isCaSplitLimit', type: 'column', dataType: 'boolean' },
    { property: 'hasNonOwnedAuto', type: 'column', dataType: 'boolean' },
    { property: 'elAnyExcluded', type: 'column', dataType: 'boolean' },
    { property: 'elEachAccident', type: 'column', dataType: 'number' },
    { property: 'elDiseaseEachEmployee', type: 'column', dataType: 'number' },
    { property: 'elDiseasePerPolicy', type: 'column', dataType: 'number' },
    { property: 'garageKeepers', type: 'column', dataType: 'number' },
    { property: 'onHookTowing', type: 'column', dataType: 'number' },
    {
      property: 'crimeDishonestyFidelityBond',
      type: 'column',
      dataType: 'number'
    },
    { property: 'wasMigrated', type: 'column', dataType: 'boolean' },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },
    { property: 'hasDescOps', type: 'column', dataType: 'boolean' },
    { property: 'nonOwnedAutoAmount', type: 'column', dataType: 'number' },
    { property: 'isContractualLiability', type: 'column', dataType: 'boolean' },
    { property: 'officerExcluded', type: 'column', dataType: 'boolean' },
    { property: 'isPerOccurrence', type: 'column', dataType: 'boolean' },
    { property: 'perProjLocAgg', type: 'column', dataType: 'boolean' },
    { property: 'primaryNonContributory', type: 'column', dataType: 'boolean' },
    { property: 'occurrenceBased', type: 'column', dataType: 'boolean' },
    {
      property: 'hasAdditionalInsuredEndorsements',
      type: 'column',
      dataType: 'boolean'
    },
    { property: 'hiredAutos', type: 'column', dataType: 'number' },
    { property: 'nonOwnedAutos', type: 'column', dataType: 'number' },
    { property: 'isWorkersCompExempt', type: 'column', dataType: 'boolean' },
    { property: 'contingentAuto', type: 'column', dataType: 'boolean' },
    { property: 'trailerInterchange', type: 'column', dataType: 'number' },
    { property: 'cargoLiability', type: 'column', dataType: 'number' },
    { property: 'legalNameMismatch', type: 'column', dataType: 'boolean' },
    { property: 'isArchived', type: 'column', dataType: 'boolean' },

    { property: 'Sites', model: 'Site', type: 'association' },
    {
      property: 'InsurancePolicySites',
      model: 'InsurancePolicySite',
      type: 'association'
    }
  ];

  static {
    InsurancePolicy.InsurancePolicyProperties.forEach((propertyObject) => {
      const { property, type } = propertyObject;
      if (type === 'column') {
        InsurancePolicy.columnMap.set(property, propertyObject);
      }
      if (type === 'alias') {
        InsurancePolicy.aliasedColumnMap.set(
          property,
          propertyObject as { property: string; type: 'alias' }
        );
      }
      if (type === 'association') {
        InsurancePolicy.associationMap.set(
          property,
          propertyObject as {
            property: string;
            type: 'association';
            model: string;
          }
        );
      }
    });
  }

  constructor(InsurancePolicyObject: Partial<InsurancePolicy>) {
    super();

    InsurancePolicy.InsurancePolicyProperties.forEach((propertyObject) => {
      const { property } = propertyObject;
      const value = InsurancePolicyObject[property];
      if (value !== undefined) {
        Object.assign(this, { [property]: value });
      }
    });
  }

  id?: number;
  InsuranceCertificateId?: number;
  policyNumber?: string | null;
  policyType?: 'GL' | 'UMB' | 'WC' | 'PL' | 'AUTO';
  effectiveDate?: Date | null;
  expirationDate?: Date | null;
  status?:
    | 'Active'
    | 'Renewal'
    | 'Expiring'
    | 'Expired'
    | 'Inactive'
    | 'Cancelled'
    | 'Archived';
  statusDate?: Date;
  insurer?: string | null;
  insurerLetter?: string | null;
  naic?: string | null;
  aMBestCompanyNumber?: string | null;
  amBestRating?:
    | 'A++'
    | 'A+'
    | 'A'
    | 'A-'
    | 'B++'
    | 'B+'
    | 'B'
    | 'B-'
    | 'C++'
    | 'C+'
    | 'C'
    | 'C-'
    | 'D'
    | 'E'
    | 'F'
    | 'S'
    | 'Not Rated'
    | null;
  financialSize?:
    | 'I'
    | 'II'
    | 'III'
    | 'IV'
    | 'V'
    | 'VI'
    | 'VII'
    | 'VIII'
    | 'IX'
    | 'X'
    | 'XI'
    | 'XII'
    | 'XIII'
    | 'XIV'
    | 'XV'
    | null;
  addlInsured?: boolean;
  subrWvd?: boolean;
  isRequired?: boolean;
  hasPolicyDoc?: boolean;
  hasRequiredForms?: boolean;
  perOccurence?: number | null;
  aggregate?: number | null;
  hasAiOngoingOps?: boolean;
  hasAiCompOps?: boolean;
  hasPnc?: boolean;
  damageToRented?: number | null;
  medicalExpenses?: number | null;
  personalAdvInjury?: number | null;
  productOps?: number | null;
  isProdOpsSameAsAgg?: boolean;
  pollution?: number | null;
  combinedSingle?: number | null;
  biPerPerson?: number | null;
  biPerAccident?: number | null;
  propertyDamage?: number | null;
  isCommercialAuto?: boolean;
  isCaSplitLimit?: boolean;
  hasNonOwnedAuto?: boolean;
  elAnyExcluded?: boolean;
  elEachAccident?: number | null;
  elDiseaseEachEmployee?: number | null;
  elDiseasePerPolicy?: number | null;
  garageKeepers?: number | null;
  onHookTowing?: number | null;
  crimeDishonestyFidelityBond?: number | null;
  wasMigrated?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  hasDescOps?: boolean = false;
  nonOwnedAutoAmount?: number | null;
  isContractualLiability?: boolean;
  officerExcluded?: boolean;
  isPerOccurrence?: boolean;
  perProjLocAgg?: boolean;
  primaryNonContributory?: boolean;
  occurrenceBased?: boolean;
  hasAdditionalInsuredEndorsements?: boolean;
  hiredAutos?: number | null;
  nonOwnedAutos?: number | null;
  InsNameFailed?: boolean;
  isWorkersCompExempt?: boolean;
  isArchived?: boolean;
  contingentAuto?: boolean;
  trailerInterchange?: number | null;
  legalNameMismatch?: boolean;
  cargoLiability?: number | null;

  Sites?: Site[];
  InsurancePolicySites?: InsurancePolicySite[];
}
